import React, { useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { connect } from 'react-redux';
import { InputSearch, Empty, MultiCollapsibleList } from '../../../../components';
import { isNullOrEmptyArray } from '../../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../../util/formatters/stringNormalizer';
import { adaptSurveysToData } from './adapters';

import * as Ui from './styles';

function MultiSurveysAwareCollapsibleList({
  /**
   * lista de pesquisas [{id, titulo}],
   */
  surveys = [],
  /**
   * Controle externo de estado, são as pesquisas que
   * devem ser marcadas automaticamente no seletor
   */
  selectedSurveys = [],
  /**
   * define o label usado para este componente como um todo
   */
  label,
  /**
   * conteúdo html ou component para ser renderizado como erro
   */
  errorComponent,
  /**
   * placeholder do input de pesquisa do selector
   */
  inputPlaceholder = 'Busque pelo título da pesquisa',
  /**
   * propaga o evento de alteração na listagem de questões selecionadas
   */
  onSelectSurveys,
  /**
   * Perguntas a serem ignoradas na listagem [1,2,3...]
   */
  surveysToIgnore = [],
  /**
   * Define se o item todos selecionados sera exibido
   */
  withAllSelected,
  /**
   * Define se o item allSelect esta ativo
   */
  allItemsSelected,
}, ref) {
  // timeout for load text in input
  let timeoutId = 0;

  // states
  const [surveysData, setSurveysData] = useState([]);
  const [searchText, setSearchText] = useState('');
  // components wrapper
  const ErrorComponentWrapped = () => (errorComponent ? (<>{errorComponent}</>) : null);

  // callbacks: initialize datas
  const initialize = useCallback((allSurveys, ignoredIds, filterText, selectedIds = []) => {
    // filter by text
    function filter(data, text) {
      if (!text) return data;
      const lowerCaseText = text.toLowerCase();
      const filterResult = data.map((item) => {
        if (!removerCaracteresEspeciais(item.label).toLowerCase().includes(lowerCaseText)) {
          return { ...item, hidden: true };
        }
        return item;
      });
      return filterResult;
    }

    /**
     * helper for build surveys data
     */
    function prepareNewQuestions() {
      // adapt surveys for this component
      const newData = adaptSurveysToData(
        [...allSurveys], selectedIds,
      );
      // remote quesions by surveyToIgnore
      ignoredIds.forEach((id) => {
        const indexToRemove = newData.findIndex((q) => q.value === id);
        newData.splice(indexToRemove, 1);
      });
      // set survey data
      setSurveysData(() => filter(newData, filterText));
    }

    // prepare customs surveys
    if (!isNullOrEmptyArray(ignoredIds) || !isNullOrEmptyArray(allSurveys)) {
      prepareNewQuestions();
    }
  }, []);


  // effect of update surveys
  useEffect(() => {
    initialize(surveys, surveysToIgnore, searchText, selectedSurveys);
  }, [initialize, surveys, surveysToIgnore, searchText, selectedSurveys]);

  // api para este componente poder
  // ser acesso pelo parent
  // on reset datas
  useImperativeHandle(ref, () => ({
    reset: () => {
      // reset search text
      setSearchText('');
      // initialize
      initialize(surveys, surveysToIgnore, '');
    },
  }));

  // handle change search text
  function handleChangeTextSearch(value) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setSearchText(value);
    }, 300);
  }

  // handle on select item
  const handleSelectSurveys = useCallback((sel, allSel) => {
    const formattedData = sel ? sel.map((item) => (typeof item === 'number' ? item : item.value)) : [];
    if (onSelectSurveys) onSelectSurveys(Array.from(new Set(formattedData)), allSel);
  }, [onSelectSurveys]);

  const handleAllSelected = useCallback((sel, checked) => {
    const formattedData = !isNullOrEmptyArray(sel) ? sel.map((item) => (typeof item === 'number' ? item : item.value)) : [];
    if (onSelectSurveys) onSelectSurveys(formattedData, checked);
  }, [onSelectSurveys]);

  if (surveys.length === 0) {
    return (
      <Empty text={'Não existem pesquisas a serem apresentadas'} />
    );
  }

  return (
    <Ui.Container>
      <Ui.HeadSection>
        {/* filtro */}
        <Ui.FilterSectionItem>
          <Ui.Label>{label}</Ui.Label>
          <InputSearch
            placeholder={inputPlaceholder}
            styleClass="inputsearch"
            onChangeText={(value) => handleChangeTextSearch(value)}
          />
        </Ui.FilterSectionItem>
      </Ui.HeadSection>
      {/* error */}
      <Ui.ErrorSection>
        <ErrorComponentWrapped />
      </Ui.ErrorSection>
      {/* list */}
      <MultiCollapsibleList
        datas={surveysData}
        onSelect={handleSelectSurveys}
        listType={'collapsibleList'}
        withAllSelected={withAllSelected}
        onSelectAll={handleAllSelected}
        setActiveItems
        allItemsSelected={allItemsSelected}
        bodyHeight={'152px'}
        withArrow={false}
      />
    </Ui.Container>
  );
}

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * surveys => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state) => {
  // get state
  const surveysState = state.surveys;
  // result
  return {
    running: surveysState.running,
    error: surveysState.error,
    surveys: surveysState.payload,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: true },
)(
  React.forwardRef(MultiSurveysAwareCollapsibleList),
);
